import type { JSX, ReactNode } from 'react';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { ExperienceViews } from './ExperienceViews';
import { experienceTableColumns } from '../config/experienceTableColumns';
import type { Experience, ExperienceStatus } from '../types/experience.types';

const EMPTY_MESSAGE = 'No experiences added yet. Click "Add Experience" to add work history.';

export interface ExperienceListProps {
  experiences: Experience[];
  /** Whether the query ever resolved. Distinguishes a cached empty list from a failed first load. */
  hasData: boolean;
  isLoading: boolean;
  isError?: boolean;
  onEdit: (experience: Experience) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: ExperienceStatus) => void;
  renderExpandedContent: (experience: Experience) => ReactNode;
}

function Message({ children, tone }: { children: ReactNode; tone?: string }): JSX.Element {
  return <div className={`text-center py-8 ${tone ?? 'text-muted-foreground'}`}>{children}</div>;
}

/**
 * Owns the filter/sort state for experiences: the list is embedded in a card section
 * rather than a page, so the filter bar lives here and feeds both table and cards.
 */
export function ExperienceList({
  experiences,
  hasData,
  isLoading,
  isError,
  ...itemProps
}: ExperienceListProps): JSX.Element {
  const { filteredSorted, sort, toggleSort, ...toolbar } = useTableFilters(
    experiences,
    experienceTableColumns
  );

  if (isLoading) return <Message>Loading experiences...</Message>;

  // A failed refetch keeps whatever resolved before, so only report the failure when nothing ever did.
  if (isError && !hasData) {
    return <Message tone="text-destructive">Failed to load experiences</Message>;
  }

  if (experiences.length === 0) {
    return (
      <div className="space-y-4">
        <ResultCount count={0} />
        <Message>{EMPTY_MESSAGE}</Message>
      </div>
    );
  }

  const viewProps = { ...itemProps, experiences: filteredSorted, sort, onToggleSort: toggleSort };

  return (
    <div className="space-y-4">
      <ResultCount count={filteredSorted.length} />
      <DataTableFilters
        columns={experienceTableColumns}
        filters={toolbar.filters}
        activeFilterCount={toolbar.activeFilterCount}
        onFilterChange={toolbar.setFilter}
        onClearFilters={toolbar.clearFilters}
        searchTerm={toolbar.searchTerm}
        onSearchChange={toolbar.setSearchTerm}
      />
      {filteredSorted.length === 0 ? (
        <Message>No experiences match the current filters.</Message>
      ) : (
        <ExperienceViews {...viewProps} />
      )}
    </div>
  );
}
